import { css } from "@emotion/react";
import { useMemo, useState } from "react";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import { Typography } from "../../Typography";
import { Paper, PaperProps } from "../../Paper";
import { List, ListItem } from "../../List";
import { Button } from "../../Button";

const Item = ({
  label,
  checked,
  onChangeChecked,
}: {
  label: string;
  checked: boolean;
  onChangeChecked: (value: boolean) => void;
}) => {
  return (
    <ListItem
      css={css`
        display: flex;
        gap: 8px;
        align-items: center;
      `}
      onClick={() => onChangeChecked(!checked)}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={(event) => onChangeChecked(event.currentTarget.checked)}
        aria-label={label}
      />
      {label}
    </ListItem>
  );
};

export interface TransferListProps<
  T extends {
    label: string;
    value: string;
  },
> {
  options?: { left?: T[]; right?: T[] };
  onChange?: (values: { left: T[]; right: T[] }) => void;
  paperProps?: PaperProps;
}

export const TransferList = <
  T extends {
    label: string;
    value: string;
  },
>({
  options,
  onChange,
  paperProps,
}: TransferListProps<T>) => {
  const [rightChecked, setRightChecked] = useState<Record<number, boolean>>({});
  const [leftChecked, setLeftChecked] = useState<Record<number, boolean>>({});

  const [rightOptions, leftOptions] = useMemo(
    () => [options?.right ?? [], options?.left ?? []],
    [options?.left, options?.right],
  );

  const handleUpdate = (left: T[], right: T[]) => {
    onChange?.({ left, right });
  };

  return (
    <div
      css={css`
        display: grid;
        grid-template-columns: 1fr auto 1fr;
        gap: 16px;
        align-items: flex-start;
      `}
    >
      <div>
        {leftOptions.length === 0 && <Typography>NO ITEMS</Typography>}
        <Paper {...paperProps}>
          <List aria-label="left itemlist">
            {leftOptions.map((item, index) => (
              <Item
                key={item.value}
                label={item.label}
                checked={leftChecked[index] ?? false}
                onChangeChecked={(value) => {
                  setLeftChecked((prev) => ({ ...prev, [index]: value }));
                }}
              />
            ))}
          </List>
        </Paper>
      </div>
      <div
        css={css`
          display: grid;
          gap: 8px;
        `}
      >
        <Button
          variant="outlined"
          icon={<KeyboardDoubleArrowLeftIcon />}
          disabled={rightOptions.length === 0}
          onClick={() => {
            handleUpdate([...leftOptions, ...rightOptions], []);
          }}
          aria-label="Move all items to left"
        />
        <Button
          variant="outlined"
          icon={<KeyboardArrowLeftIcon />}
          disabled={Object.values(rightChecked).filter(Boolean).length === 0}
          onClick={() => {
            const rightToLeft: T[] = [];
            const rightToRight: T[] = [];
            rightOptions.forEach((item, index) => {
              if (rightChecked[index]) {
                rightToLeft.push(item);
              } else {
                rightToRight.push(item);
              }
            });

            setRightChecked([]);
            handleUpdate([...leftOptions, ...rightToLeft], rightToRight);
          }}
          aria-label="Move selected items to left"
        />
        <Button
          variant="outlined"
          icon={<KeyboardArrowRightIcon />}
          disabled={Object.values(leftChecked).filter(Boolean).length === 0}
          onClick={() => {
            const leftToRight: T[] = [];
            const leftToLeft: T[] = [];
            leftOptions.forEach((item, index) => {
              if (leftChecked[index]) {
                leftToRight.push(item);
              } else {
                leftToLeft.push(item);
              }
            });

            setLeftChecked([]);
            handleUpdate(leftToLeft, [...rightOptions, ...leftToRight]);
          }}
          aria-label="Move selected items to right"
        />
        <Button
          variant="outlined"
          icon={<KeyboardDoubleArrowRightIcon />}
          disabled={leftOptions.length === 0}
          onClick={() => {
            handleUpdate([], [...rightOptions, ...leftOptions]);
          }}
          aria-label="Move all items to right"
        />
      </div>
      <div>
        {rightOptions.length === 0 && <Typography>NO ITEMS</Typography>}
        <Paper {...paperProps}>
          <List aria-label="right itemlist">
            {rightOptions.map((item, index) => (
              <Item
                key={item.value}
                label={item.label}
                checked={rightChecked[index] ?? false}
                onChangeChecked={(value) => {
                  setRightChecked((prev) => ({ ...prev, [index]: value }));
                }}
              />
            ))}
          </List>
        </Paper>
      </div>
    </div>
  );
};
